import React from 'react';
import styled from 'styled-components';
import {getColor} from '../utils/styled';
import {GRAY_60, GRAY150, LOCHMARA, PURE_WHITE} from '../refs/Colors';
import {Toggleable} from "@/types/Toggleable.ts";

type SwitchProps = {
  className?: string;
  disabled?: boolean;
  toggleable: Toggleable;
  children?: React.ReactNode;
};

const TRACK_WIDTH = 33;
const TRACK_HEIGHT = 16;
const THUMB_SIZE = 12;

const Label = styled.label`
  position: relative;
  display: inline-block;
  cursor: pointer;
  padding-left: ${TRACK_WIDTH + 8}px;
  
  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const Checkbox = styled.input`
  display: none;
  opacity: 0;
  position: absolute;
  left: 0;
  top: 50%;
  width: ${TRACK_WIDTH}px;
  height: ${TRACK_HEIGHT}px;
  outline: none;
  z-index: 2;
  transform: translate(0, -50%);
`;

const Span = styled.span`
  display: inline-block;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: ${getColor(GRAY150)};
  margin: 0;

  &::before,
  &::after
  {
    content: "";
    display: block;
    position: absolute;
  }

  &::before {
    width: ${TRACK_WIDTH}px;
    height: ${TRACK_HEIGHT}px;
    background-color: ${getColor(GRAY_60)};
    border-radius: 50em;
    left: 0;
    top: 50%;
    transform: translate(0, -50%);
  }

  &::after {
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    background-color: ${getColor(PURE_WHITE)};
    left: 0;
    top: 50%;
    border-radius: 50%;
    transform: translate(2px, -50%);
    transition: transform 0.3s;
  }

  ${Checkbox}:checked + &::before {
    background-color: ${getColor(LOCHMARA)};
  }

  ${Checkbox}:checked + &::after {
    transform: translate(${TRACK_WIDTH - THUMB_SIZE - 2}px, -50%);
  }
`;

export const Switch: React.FC<SwitchProps> = ({
  className,
  toggleable,
  disabled,
  children
}) => {
  const handleClick = (evt:React.MouseEvent) => {
    evt.preventDefault();
    if(!disabled) {
      toggleable.toggle?.();
    }
  };

  return (
    <Label className={[className, disabled ? 'disabled' : ''].join(' ')} onClick={handleClick}>
      <Checkbox
        type="checkbox"
        checked={toggleable.isOpen}
        disabled={disabled}
      />
      <Span>{children}</Span>
    </Label>
  );
};

export default Switch;
